(() => {
  'use strict';
  const SAMPLE_HANDLE = 'first-flight-pack';
  const PASSION_POUCH_VARIANTS = new Set(['45549172883762', '45549172916530', '45549172949298']);

  function createCartClient(fetcher, root = '/') {
    const base = /^\/(?!\/)/.test(root) ? `${root.replace(/\/$/, '')}/` : '/';
    let pendingAdd;

    async function request(path, payload) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetcher(`${base}${path}`, {
          method: payload ? 'POST' : 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
          ...(payload ? { body: JSON.stringify(payload) } : {}),
          signal: controller.signal
        });
        const data = await response.json();
        if (!response.ok || Number(data.status) >= 400) {
          const detail = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'Please try again.';
          throw new Error(detail);
        }
        return data;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('The request timed out. Please check your bag before trying again.');
        throw error;
      } finally {
        clearTimeout(timeout);
      }
    }

    function validCart(data) {
      if (!Array.isArray(data.items) || !Number.isFinite(data.total_price) || typeof data.currency !== 'string') {
        throw new Error('Your bag could not be loaded. Please try again.');
      }
      return data;
    }

    async function getCart() {
      return validCart(await request('cart.js'));
    }

    async function getSample() {
      let product;
      try {
        product = await request(`products/${SAMPLE_HANDLE}.js`);
      } catch {
        throw new Error('The sample pack is currently unavailable. Please try again later.');
      }
      const variant = product.variants?.find(item => item.available && Number.isSafeInteger(item.id) && Number.isFinite(item.price));
      if (product.handle !== SAMPLE_HANDLE || !product.published_at || !product.available || !variant || product.requires_selling_plan) {
        throw new Error('The sample pack is currently unavailable. Please try again later.');
      }
      return { product, variant };
    }

    async function changeItem(key, quantity) {
      if (typeof key !== 'string' || !key || !Number.isInteger(quantity) || quantity < 0) throw new Error('This item could not be updated. Please refresh your bag.');
      return validCart(await request('cart/change.js', { id: key, quantity }));
    }

    function sampleItems(state) {
      return state.items.filter(item => item.handle === SAMPLE_HANDLE);
    }

    async function enforceSampleLimit(state) {
      for (let attempts = 0; attempts < 20; attempts += 1) {
        const samples = sampleItems(state);
        const extra = samples[1];
        if (extra) state = await changeItem(extra.key, 0);
        else if (samples[0]?.quantity > 1) state = await changeItem(samples[0].key, 1);
        else return state;
      }
      throw new Error('Please keep one sample pack in your bag before checking out.');
    }

    function addSample() {
      if (pendingAdd) return pendingAdd;
      pendingAdd = (async () => {
        const sample = await getSample();
        const current = await getCart();
        if (sampleItems(current).length) return { cart: await enforceSampleLimit(current), sample, added: false };
        await request('cart/add.js', { items: [{ id: sample.variant.id, quantity: 1 }] });
        const cart = await enforceSampleLimit(await getCart());
        if (!sampleItems(cart).length) throw new Error('The sample could not be confirmed in your bag. Please check your bag before trying again.');
        return { cart, sample, added: true };
      })().finally(() => { pendingAdd = undefined; });
      return pendingAdd;
    }

    return { getCart, getSample, changeItem, addSample, enforceSampleLimit, checkoutUrl: `${base}checkout` };
  }

  if (typeof module === 'object' && module.exports) module.exports = { createCartClient };
  if (typeof document === 'undefined') return;
  const $ = selector => document.querySelector(selector);
  const $$ = selector => [...document.querySelectorAll(selector)];
  const cart = $('.cart-dialog');
  if (!cart) return;
  const config = $('[data-sample-config]');
  const client = createCartClient(window.fetch.bind(window), window.Shopify?.routes?.root || config?.dataset.root || '/');
  let cartState;
  let sample;
  let busy = false;
  let currency = config?.dataset.currency || 'USD';
  let previousFocus;

  function money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
  }

  function showMessage(message, isError = false) {
    const box = $('[data-cart-message]');
    box.textContent = message;
    box.hidden = !message;
    box.setAttribute('role', isError ? 'alert' : 'status');
    $('#cart-status').textContent = message;
  }

  function updateButtons() {
    $$('[data-add]').forEach(button => {
      button.disabled = busy || !sample;
      button.setAttribute('aria-busy', String(busy));
      const label = button.querySelector('[data-add-label]');
      label.textContent = busy ? 'Updating your bag…' : sample ? `${button.dataset.addLabel} — ${money(sample.variant.price)}` : 'Sample currently unavailable';
    });
    $('.checkout-preview').disabled = busy || !cartState?.items.length;
    $$('[data-remove-key]').forEach(button => { button.disabled = busy; });
  }

  function updatePrice() {
    if (!sample) return;
    $$('[data-sample-price]').forEach(element => { element.textContent = money(sample.variant.price); });
    updateButtons();
  }

  function node(tag, text, className) {
    const element = document.createElement(tag);
    if (text !== undefined) element.textContent = text;
    if (className) element.className = className;
    return element;
  }

  function renderCart(state) {
    cartState = state;
    currency = state.currency;
    const items = $('[data-cart-items]');
    items.replaceChildren();
    state.items.forEach(item => {
      const row = node('div', undefined, 'cart-item');
      const photo = node('div', undefined, 'cart-photo');
      const imageUrl = PASSION_POUCH_VARIANTS.has(String(item.variant_id ?? item.id)) ? config?.dataset.approvedPassionPouch : item.image;
      if (imageUrl) {
        const image = node('img');
        image.src = imageUrl;
        image.alt = item.product_title || item.title;
        image.width = 78;
        image.height = 120;
        photo.append(image);
      }
      const details = node('div');
      details.append(node('h3', item.product_title || item.title));
      if (item.variant_title && item.variant_title !== 'Default Title') details.append(node('p', item.variant_title));
      if (item.selling_plan_allocation?.selling_plan?.name) details.append(node('p', item.selling_plan_allocation.selling_plan.name));
      Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value).forEach(([key, value]) => details.append(node('p', `${key}: ${value}`)));
      details.append(node('span', `Quantity: ${item.quantity}`));
      const remove = node('button', 'Remove', 'remove-item');
      remove.type = 'button';
      remove.dataset.removeKey = item.key;
      remove.setAttribute('aria-label', `Remove ${item.product_title || item.title}`);
      details.append(remove);
      row.append(photo, details, node('strong', money(item.final_line_price ?? item.line_price)));
      items.append(row);
    });
    const quantity = state.items.reduce((total, item) => total + item.quantity, 0);
    $('.cart-empty').hidden = quantity > 0;
    $('.cart-filled').hidden = quantity === 0;
    $('.bag-count').hidden = quantity === 0;
    $('.bag-count').textContent = String(quantity);
    $('.cart-toggle').setAttribute('aria-label', `Open shopping bag, ${quantity} ${quantity === 1 ? 'item' : 'items'}`);
    $('[data-cart-subtotal]').textContent = money(state.total_price);
    const discounts = $('[data-cart-discounts]');
    discounts.textContent = state.total_discount > 0 ? `${money(state.total_discount)} in discounts included.` : '';
    discounts.hidden = !state.total_discount;
    const sampleInBag = state.items.some(item => item.handle === SAMPLE_HANDLE);
    $('[data-sample-cart-note]').hidden = !sampleInBag;
    updatePrice();
    updateButtons();
  }

  async function refreshCart() {
    const state = await client.getCart();
    renderCart(state);
    return state;
  }

  async function runAction(action) {
    if (busy) return;
    busy = true;
    updateButtons();
    showMessage('');
    try {
      await action();
    } catch (error) {
      showMessage(error.message || 'Your bag could not be updated. Please try again.', true);
      try { await refreshCart(); } catch {}
    } finally {
      busy = false;
      updateButtons();
    }
  }
  function openDialog(dialog) {
    previousFocus = document.activeElement;
    if (!dialog.open) dialog.showModal();
  }
  $$('[data-add]').forEach(button => button.addEventListener('click', () => {
    openDialog(cart);
    runAction(async () => {
      const result = await client.addSample();
      sample = result.sample;
      renderCart(result.cart);
      showMessage(result.added ? 'First Flight Pack added to your bag.' : 'Your bag already contains one First Flight Pack.');
    });
  }));
  $$('[data-cart-open]').forEach(button => button.addEventListener('click', () => {
    openDialog(cart);
    runAction(refreshCart);
  }));
  $('[data-preview-open]').addEventListener('click', () => openDialog($('.preview-dialog')));
  $$('[data-close]').forEach(button => button.addEventListener('click', () => button.closest('dialog').close()));
  $$('dialog').forEach(dialog => {
    dialog.addEventListener('click', event => {
      const rect = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
    });
    dialog.addEventListener('close', () => previousFocus?.focus());
  });
  $('[data-cart-items]').addEventListener('click', event => {
    const button = event.target.closest('[data-remove-key]');
    if (!button) return;
    runAction(async () => {
      renderCart(await client.changeItem(button.dataset.removeKey, 0));
      showMessage('Item removed from your bag.');
      if (!cartState.items.length) $('.cart-empty [data-close]').focus();
    });
  });
  $('.checkout-preview').addEventListener('click', () => runAction(async () => {
    const state = await client.enforceSampleLimit(await client.getCart());
    renderCart(state);
    if (!state.items.length) throw new Error('Your bag is empty. Add an item before checking out.');
    window.location.assign(client.checkoutUrl);
  }));

  const tabs = $$('[data-gallery]');
  function selectTab(tab) {
    tabs.forEach(item => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      document.getElementById(item.getAttribute('aria-controls')).hidden = !selected;
    });
  }
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => selectTab(tab));
    tab.addEventListener('keydown', event => {
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (next !== undefined) { event.preventDefault(); selectTab(tabs[next]); tabs[next].focus(); }
    });
  });
  const menuButton = $('.mobile-menu-button');
  function closeMenu() { menuButton.setAttribute('aria-expanded', 'false'); menuButton.setAttribute('aria-label', 'Open menu'); $('#main-nav').classList.remove('is-open'); }
  menuButton.addEventListener('click', () => {
    const open = menuButton.getAttribute('aria-expanded') !== 'true';
    menuButton.setAttribute('aria-expanded', String(open));
    menuButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    $('#main-nav').classList.toggle('is-open', open);
  });
  $$('#main-nav a').forEach(link => link.addEventListener('click', closeMenu));
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && menuButton.getAttribute('aria-expanded') === 'true') { closeMenu(); menuButton.focus(); }
  });
  const sticky = $('.sticky-buy');
  let queued = false;
  function updateSticky() {
    const mainButton = $('.primary-buy').getBoundingClientRect();
    const end = $('.last-call').getBoundingClientRect();
    sticky.hidden = !(mainButton.bottom < 0 && end.top > window.innerHeight - 80);
    queued = false;
  }
  window.addEventListener('scroll', () => { if (!queued) { requestAnimationFrame(updateSticky); queued = true; } }, { passive: true });
  window.addEventListener('resize', updateSticky);
  Promise.allSettled([client.getSample(), client.getCart()]).then(results => {
    if (results[0].status === 'fulfilled') {
      sample = results[0].value;
      $('[data-offer-status]').textContent = '';
    } else {
      $('[data-offer-status]').textContent = results[0].reason.message;
    }
    if (results[1].status === 'fulfilled') {
      renderCart(results[1].value);
      if (!busy) showMessage('');
    }
    else showMessage('Your bag could not be loaded. Open the bag to try again.', true);
    updatePrice();
    updateButtons();
  });
  updateSticky();
})();
